import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Security } from '@okta/okta-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { FullStory, init as initFullStory } from '@fullstory/browser';

import App from 'app';
import * as serviceWorker from 'service-worker';

import { isFullStoryEnabled } from 'helpers/fullstory';
import { getOktaUserId } from 'helpers/okta-session';
import { restoreOriginalUri } from 'helpers/restore-original-uri';

import queryClient from 'utils/query-client';
import powerbi from 'utils/powerbi';
import oktaAuth from 'utils/okta/use-okta';

import MuiProviders from 'theme/mui-providers';

import 'styles/index.css';
import 'react-tooltip/dist/react-tooltip.css';

// Okta's `OnAuthRequiredFunction` signature expects `void | Promise<void>`.
// The legacy JS code returned a `<Navigate>` element here, but Okta discards
// the return value — the element was constructed and thrown away. Navigation
// to /login is actually handled by `<RequireAuth>` and the route table.
const onAuthRequired = (): void => {};

powerbi.preload({
  type: 'report',
  embedUrl: 'https://app.powerbi.com/reportEmbed',
});

const oktaUserId = getOktaUserId();

if (isFullStoryEnabled()) {
  initFullStory({ orgId: 'o-1CBCVA-na1' });

  const storedTokens = window.localStorage.getItem('okta-token-storage');
  const oktaStorage = storedTokens ? JSON.parse(storedTokens) : null;
  const email = oktaStorage?.idToken?.claims?.email;

  if (oktaUserId) {
    FullStory('setIdentity', { uid: oktaUserId, properties: { email } });
  }
}

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element #root not found in document');
}

const root = ReactDOM.createRoot(rootElement);

root.render(
  <MuiProviders>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Security
          oktaAuth={oktaAuth}
          onAuthRequired={onAuthRequired}
          restoreOriginalUri={restoreOriginalUri}
        >
          <App />
        </Security>
      </BrowserRouter>
    </QueryClientProvider>
  </MuiProviders>
);

serviceWorker.unregister();
